/* Floating buy-box (mobile).
   Delegates user actions to the existing on-page <variant-selects> radios and
   <form is="product-form">. Subscribes to the theme's variantChange pubsub to
   keep the CTA price, fit-confidence size token, and active size pill in sync. */

(function () {
  if (customElements.get("floating-buy-box")) return;

  class FloatingBuyBox extends HTMLElement {
    connectedCallback() {
      this.unsubscribers = [];
      this.bindSizePills();
      this.bindAssistant();
      this.bindCta();
      this.subscribeToVariantChange();
      this.subscribeToCartUpdate();
      this.measureHeight();
      this.bindScrollVisibility();
      this.observeBuySection();
      this.preselectDefaultSize();
    }

    disconnectedCallback() {
      this.unsubscribers.forEach((fn) => {
        try {
          fn();
        } catch (_) {}
      });
      if (this.resizeObserver) this.resizeObserver.disconnect();
      if (this.buySectionObserver) this.buySectionObserver.disconnect();
      window.removeEventListener("resize", this.boundMeasure);
      if (this.boundScroll) {
        window.removeEventListener("scroll", this.boundScroll);
        window.removeEventListener("resize", this.boundScroll);
      }
    }

    bindSizePills() {
      this.querySelectorAll("[data-fbb-size]").forEach((pill) => {
        pill.addEventListener("click", () => {
          const value = pill.dataset.fbbSize;
          const optionName = pill.dataset.fbbOptionName;
          const radio = document.querySelector(
            `variant-selects input[type="radio"][name="${cssEscape(optionName)}"][value="${cssEscape(value)}"]`,
          );
          if (!radio) return;
          triggerVariantChange(radio);
        });
      });
    }

    bindAssistant() {
      const local = this.querySelector(".fbb__assistant");
      if (!local) return;
      local.addEventListener("click", () => {
        const original =
          document.querySelector("variant-selects .custom-size-button") ||
          document.querySelector(".custom-size-button");
        if (original && original !== local) original.click();
      });
    }

    bindCta() {
      const cta = this.querySelector("[data-fbb-cta]");
      if (!cta) return;
      cta.addEventListener("click", () => {
        const target = document.querySelector("[data-vpv2-hidden]");
        if (!target) return;
        const headroom = Math.min(140, Math.round(window.innerHeight * 0.18));
        const rect = target.getBoundingClientRect();
        const top = rect.top + window.scrollY - headroom;
        window.scrollTo({ top, behavior: "smooth" });

        const highlights = [
          target,
          document.querySelector(".product-form-wrapper"),
        ].filter(Boolean);
        highlights.forEach((el) => {
          el.classList.remove("fbb-attention");
          // Force reflow so the animation restarts on repeated clicks.
          void el.offsetWidth;
          el.classList.add("fbb-attention");
        });
        clearTimeout(this.attentionTimer);
        this.attentionTimer = setTimeout(() => {
          highlights.forEach((el) => el.classList.remove("fbb-attention"));
        }, 3200);
      });
    }

    observeBuySection() {
      const target = document.querySelector("[data-vpv2-hidden]");
      if (!target || !("IntersectionObserver" in window)) return;
      this.buySectionObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            this.classList.toggle("is-near-buy-section", entry.isIntersecting);
          });
        },
        { threshold: 0.15 },
      );
      this.buySectionObserver.observe(target);
    }

    subscribeToVariantChange() {
      const handler = (payload) => {
        const variant =
          payload &&
          (payload.variant || (payload.data && payload.data.variant));
        if (!variant) return;
        this.updateActivePill(variant);
        this.updateCta(variant);
        this.updateFit(variant);
      };

      // 1) Theme pubsub
      if (
        window.theme &&
        theme.pubsub &&
        typeof theme.pubsub.subscribe === "function"
      ) {
        const evtName =
          (theme.pubsub.PUB_SUB_EVENTS &&
            theme.pubsub.PUB_SUB_EVENTS.variantChange) ||
          "variantChange";
        const unsub = theme.pubsub.subscribe(evtName, (data) =>
          handler(data && data.data),
        );
        if (typeof unsub === "function") this.unsubscribers.push(unsub);
      }
      // 2) DOM CustomEvent fallback
      const onVariantChange = (e) => handler(e.detail);
      document.addEventListener("variant:change", onVariantChange);
      this.unsubscribers.push(() =>
        document.removeEventListener("variant:change", onVariantChange),
      );
    }

    sizePosition() {
      return Math.max(parseInt(this.dataset.sizePosition || "1", 10), 1) - 1;
    }

    selectedSize(variant) {
      if (!variant || !Array.isArray(variant.options)) return "";
      return variant.options[this.sizePosition()] || "";
    }

    updateActivePill(variant) {
      const value = this.selectedSize(variant);
      this.querySelectorAll("[data-fbb-size]").forEach((pill) => {
        const isActive = pill.dataset.fbbSize === value;
        pill.classList.toggle("is-active", isActive);
        pill.setAttribute("aria-pressed", String(isActive));
      });
    }

    updateCta(variant) {
      const cta = this.querySelector("[data-fbb-cta]");
      const label = this.querySelector("[data-fbb-cta-label]");
      if (!cta || !label) return;
      if (variant.available === false) {
        cta.disabled = true;
        label.textContent =
          (window.theme &&
            theme.variantStrings &&
            theme.variantStrings.soldOut) ||
          "Sold out";
      } else {
        cta.disabled = false;
        const tpl = this.dataset.ctaTemplate || "Add to cart — {price}";
        label.textContent = tpl.replace("{price}", formatMoney(variant.price));
      }
    }

    updateFit(variant) {
      const span = this.querySelector("[data-fbb-fit-text]");
      if (!span) return;
      const tpl =
        this.dataset.fitTemplate ||
        "{percent}% of buyers say L fits true to size";
      const pct = this.dataset.fitPercent || "";
      const size = this.selectedSize(variant);
      span.textContent = tpl.replace("{percent}", pct).replace("{size}", size);
    }

    preselectDefaultSize() {
      // Respect an explicit variant in the URL — don't override deep-links.
      if (/[?&]variant=/.test(window.location.search)) return;

      const pills = this.querySelectorAll("[data-fbb-size]");
      const target = Array.from(pills).find((p) => {
        const v = (p.dataset.fbbSize || "").trim().toLowerCase();
        return v === "l" || v === "large";
      });
      if (!target || target.classList.contains("is-active")) return;

      const optionName = target.dataset.fbbOptionName;
      const radio = document.querySelector(
        `variant-selects input[type="radio"][name="${cssEscape(optionName)}"][value="${cssEscape(target.dataset.fbbSize)}"]`,
      );
      if (!radio) return;
      triggerVariantChange(radio);
    }

    subscribeToCartUpdate() {
      const shipping = this.querySelector("[data-fbb-shipping]");
      if (!shipping) return;

      const apply = (totalCents) => {
        const threshold =
          parseInt(shipping.dataset.shippingThresholdCents, 10) || 0;
        if (!threshold) return;
        const met = totalCents >= threshold;
        const pct = met
          ? 100
          : Math.max(0, Math.min(100, Math.round((totalCents / threshold) * 100)));

        shipping.classList.toggle("is-met", met);
        shipping.classList.toggle("is-below", !met);

        const bar = shipping.querySelector("progress-bar");
        if (bar) bar.style.setProperty("--progress", pct + "%");

        const span = shipping.querySelector(".free-shipping-bar > span");
        if (span) {
          if (met) {
            span.innerHTML = shipping.dataset.shippingCongrats || "";
          } else {
            const remaining = threshold - totalCents;
            const tpl = shipping.dataset.shippingRemainingTpl || "";
            span.innerHTML = tpl.replace("__AMT__", formatMoney(remaining));
          }
        }
      };

      const handler = (payload) => {
        const cart = payload && (payload.cart || (payload.data && payload.data.cart));
        if (cart && typeof cart.total_price === "number") {
          apply(cart.total_price);
          return;
        }
        // Fallback: fetch cart if the payload didn't include it.
        fetch("/cart.js", { headers: { Accept: "application/json" } })
          .then((r) => r.json())
          .then((data) => {
            if (data && typeof data.total_price === "number") apply(data.total_price);
          })
          .catch(() => {});
      };

      if (
        window.theme &&
        theme.pubsub &&
        typeof theme.pubsub.subscribe === "function"
      ) {
        const evtName =
          (theme.pubsub.PUB_SUB_EVENTS &&
            theme.pubsub.PUB_SUB_EVENTS.cartUpdate) ||
          "cartUpdate";
        const unsub = theme.pubsub.subscribe(evtName, handler);
        if (typeof unsub === "function") this.unsubscribers.push(unsub);
      }
    }

    bindScrollVisibility() {
      const THRESHOLD = 0.05;
      let ticking = false;

      const update = () => {
        ticking = false;
        const scrollable = Math.max(
          document.documentElement.scrollHeight - window.innerHeight,
          0,
        );
        const ratio = scrollable > 0 ? window.scrollY / scrollable : 0;
        this.classList.toggle("is-visible", ratio >= THRESHOLD);
      };

      this.boundScroll = () => {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(update);
      };

      update();
      window.addEventListener("scroll", this.boundScroll, { passive: true });
      window.addEventListener("resize", this.boundScroll);
    }

    measureHeight() {
      this.boundMeasure = () => {
        const h = this.offsetHeight;
        if (h > 0)
          document.documentElement.style.setProperty("--fbb-height", h + "px");
      };
      this.boundMeasure();
      if (window.ResizeObserver) {
        this.resizeObserver = new ResizeObserver(this.boundMeasure);
        this.resizeObserver.observe(this);
      }
      window.addEventListener("resize", this.boundMeasure);
    }
  }

  function formatMoney(priceCents) {
    if (typeof priceCents !== "number")
      priceCents = parseInt(priceCents, 10) || 0;
    if (window.Shopify && typeof Shopify.formatMoney === "function") {
      const fmt =
        (window.theme && theme.moneyFormat) ||
        (window.Shopify && Shopify.money_format) ||
        "${{amount}}";
      try {
        return Shopify.formatMoney(priceCents, fmt);
      } catch (_) {}
    }
    return "$" + (priceCents / 100).toFixed(2);
  }

  function cssEscape(value) {
    if (window.CSS && typeof CSS.escape === "function")
      return CSS.escape(value);
    return String(value).replace(/["\\]/g, "\\$&");
  }

  /* Drive the variant change pipeline directly. The on-page
     <variant-selects> is wrapped in a display:none container on the v2
     PDP — change-event bubbling through a hidden subtree was unreliable.
     We try every available path so at least one lands. */
  function triggerVariantChange(radio) {
    if (!radio) {
      console.warn("[fbb] radio not found — abort");
      return;
    }
    console.log("[fbb] triggerVariantChange — radio found:", radio);

    // Path 1: form state.
    radio.checked = true;

    // Path 2: dispatch change for direct/delegated listeners.
    try {
      radio.dispatchEvent(
        new Event("change", { bubbles: true, composed: true }),
      );
    } catch (e) {
      console.warn("[fbb] dispatchEvent(change) threw:", e);
    }

    // Path 3: also click the associated label.
    var label =
      radio.id &&
      document.querySelector('label[for="' + cssEscape(radio.id) + '"]');
    if (label) {
      try {
        label.click();
      } catch (e) {
        console.warn("[fbb] label.click() threw:", e);
      }
    }

    // Path 4: publish the pubsub directly so ProductInfo runs even when
    // bubbling fails.
    var hasPubsub = !!(
      window.theme &&
      theme.pubsub &&
      theme.pubsub.publish &&
      theme.pubsub.PUB_SUB_EVENTS
    );
    console.log("[fbb] hasPubsub:", hasPubsub);
    if (!hasPubsub) return;
    var variantSelects = radio.closest && radio.closest("variant-selects");
    if (!variantSelects) {
      console.warn("[fbb] no <variant-selects> ancestor for radio");
      return;
    }
    var checked = variantSelects.querySelectorAll(
      "select option[selected], fieldset input:checked",
    );
    var selectedOptionValues = Array.prototype.map
      .call(checked, function (el) {
        return el.getAttribute("data-option-value-id");
      })
      .filter(Boolean);
    console.log("[fbb] publishing optionValueSelectionChange", {
      selectedOptionValues: selectedOptionValues,
    });
    theme.pubsub.publish(
      theme.pubsub.PUB_SUB_EVENTS.optionValueSelectionChange,
      {
        data: {
          event: { target: radio },
          target: radio,
          selectedOptionValues: selectedOptionValues,
        },
      },
    );
  }

  customElements.define("floating-buy-box", FloatingBuyBox);
})();
